import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useCRM } from '../context/CRMContext';
import { format } from 'date-fns';
import Card from '../components/UI/Card';
import LoadingSpinner from '../components/UI/LoadingSpinner';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { 
  FiUser, FiMail, FiPhone, FiMapPin, FiGlobe, FiCreditCard, FiFileText, 
  FiGitPullRequest, FiCheckSquare, FiActivity, FiArrowLeft, FiDollarSign
} = FiIcons;

const ClientProfile = () => {
  const { clientId } = useParams();
  const { clients, contracts, works, changeRequests, tasks, loading } = useCRM();
  const [clientData, setClientData] = useState(null);

  useEffect(() => {
    if (!loading && clients.length > 0) {
      const client = clients.find(c => c.id === clientId);
      if (client) {
        const clientContracts = contracts.filter(c => c.clientId === clientId);
        const contractIds = clientContracts.map(c => c.id);
        
        const clientWorks = works.filter(w => contractIds.includes(w.contractId));
        const clientChangeRequests = changeRequests.filter(cr => contractIds.includes(cr.contractId));
        
        const clientTasks = tasks.filter(t => 
          (t.projectId && client.name && t.projectId.toLowerCase().includes(client.name.toLowerCase())) || 
          clientContracts.some(c => t.projectId && c.title && t.projectId.toLowerCase() === c.title.toLowerCase())
        );

        setClientData({
          client,
          contracts: clientContracts,
          works: clientWorks,
          changeRequests: clientChangeRequests,
          tasks: clientTasks,
        });
      }
    }
  }, [clientId, clients, contracts, works, changeRequests, tasks, loading]);

  if (loading || !clientData) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  const { client, contracts: clientContracts, works: clientWorks, changeRequests: clientChangeRequests, tasks: clientTasks } = clientData;

  const stats = {
    totalContracts: clientContracts.length,
    activeContracts: clientContracts.filter(c => c.status === 'active').length,
    totalValue: clientContracts.reduce((sum, c) => sum + (parseFloat(c.value) || 0), 0),
    totalWorks: clientWorks.length,
    totalChangeRequests: clientChangeRequests.length,
    totalTasks: clientTasks.length,
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-800';
      case 'completed': return 'bg-blue-100 text-blue-800';
      case 'cancelled': return 'bg-red-100 text-red-800';
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'in-progress': return 'bg-blue-100 text-blue-800';
      case 'not-started': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <Link to="/clients" className="flex items-center text-primary-600 hover:underline">
          <SafeIcon icon={FiArrowLeft} className="h-5 w-5 mr-2" />
          Back to Clients
        </Link>
      </div>

      <Card className="p-6">
        <div className="flex items-center space-x-4">
          <div className="h-16 w-16 bg-primary-600 rounded-full flex items-center justify-center flex-shrink-0">
            <span className="text-white text-2xl font-bold">
              {client.name?.split(' ').map(n => n[0]).join('').toUpperCase()}
            </span>
          </div>
          <div>
            <h1 className="text-3xl font-bold text-gray-900">{client.name}</h1>
            {client.company && <p className="text-lg text-gray-600">{client.company}</p>}
          </div>
        </div>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-1 space-y-6">
          <Card className="p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
              <SafeIcon icon={FiUser} className="h-5 w-5 mr-2" />
              Client Details
            </h2>
            <div className="space-y-3 text-sm">
              <div className="flex items-start"><SafeIcon icon={FiMail} className="h-4 w-4 mr-2 mt-0.5 text-gray-400" /><span className="text-gray-700">{client.email}</span></div>
              <div className="flex items-start"><SafeIcon icon={FiPhone} className="h-4 w-4 mr-2 mt-0.5 text-gray-400" /><span className="text-gray-700">{client.phone || 'N/A'}</span></div>
              <div className="flex items-start"><SafeIcon icon={FiGlobe} className="h-4 w-4 mr-2 mt-0.5 text-gray-400" /><span className="text-gray-700">{client.country}</span></div>
              <div className="flex items-start"><SafeIcon icon={FiCreditCard} className="h-4 w-4 mr-2 mt-0.5 text-gray-400" /><span className="text-gray-700">TRN: {client.trn || 'N/A'}</span></div>
              <div className="flex items-start"><SafeIcon icon={FiMapPin} className="h-4 w-4 mr-2 mt-0.5 text-gray-400" /><span className="text-gray-700">{client.address || 'N/A'}</span></div>
            </div>
          </Card>

          <Card className="p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">At a Glance</h2>
            <div className="space-y-4">
              <div className="flex items-center justify-between"><span className="text-sm font-medium text-gray-600">Total Contracts</span><span className="text-lg font-bold text-gray-900">{stats.totalContracts}</span></div>
              <div className="flex items-center justify-between"><span className="text-sm font-medium text-gray-600">Active Contracts</span><span className="text-lg font-bold text-green-600">{stats.activeContracts}</span></div>
              <div className="flex items-center justify-between"><span className="text-sm font-medium text-gray-600">Total Works</span><span className="text-lg font-bold text-gray-900">{stats.totalWorks}</span></div>
              <div className="flex items-center justify-between"><span className="text-sm font-medium text-gray-600">Change Requests</span><span className="text-lg font-bold text-gray-900">{stats.totalChangeRequests}</span></div>
              <div className="flex items-center justify-between"><span className="text-sm font-medium text-gray-600">Tasks</span><span className="text-lg font-bold text-gray-900">{stats.totalTasks}</span></div>
              <div className="flex items-center justify-between pt-4 border-t"><span className="text-sm font-medium text-gray-600">Total Contract Value</span><span className="text-lg font-bold text-primary-600">${stats.totalValue.toLocaleString()}</span></div>
            </div>
          </Card>
        </div>

        <div className="lg:col-span-2 space-y-6">
          <Card className="p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center"><SafeIcon icon={FiFileText} className="h-5 w-5 mr-2" />Contracts</h2>
            <div className="space-y-3 max-h-64 overflow-y-auto">
              {clientContracts.length > 0 ? clientContracts.map(c => (
                <div key={c.id} className="p-3 bg-gray-50 rounded-lg"><div className="flex justify-between items-start"><div><p className="font-medium text-gray-900">{c.title}</p><p className="text-xs text-gray-500">{format(new Date(c.startDate), 'MMM yyyy')} - {format(new Date(c.endDate), 'MMM yyyy')}</p></div><div className="text-right"><span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(c.status)}`}>{c.status}</span><p className="text-sm font-semibold mt-1">${(parseFloat(c.value) || 0).toLocaleString()}</p></div></div></div>
              )) : <p className="text-sm text-gray-500 text-center py-4">No contracts found.</p>}
            </div>
          </Card>
          
          <Card className="p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center"><SafeIcon icon={FiActivity} className="h-5 w-5 mr-2" />Works & Services</h2>
            <div className="space-y-3 max-h-64 overflow-y-auto">
              {clientWorks.length > 0 ? clientWorks.map(w => (
                <div key={w.id} className="p-3 bg-gray-50 rounded-lg"><div className="flex justify-between items-start"><div><p className="font-medium text-gray-900">{w.title}</p><p className="text-xs text-gray-500">Started: {format(new Date(w.startDate), 'MMM dd, yyyy')}</p></div><span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(w.status)}`}>{w.status.replace('-', ' ')}</span></div></div>
              )) : <p className="text-sm text-gray-500 text-center py-4">No works found.</p>}
            </div>
          </Card>

          <Card className="p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center"><SafeIcon icon={FiGitPullRequest} className="h-5 w-5 mr-2" />Change Requests</h2>
            <div className="space-y-3 max-h-64 overflow-y-auto">
              {clientChangeRequests.length > 0 ? clientChangeRequests.map(cr => (
                <div key={cr.id} className="p-3 bg-gray-50 rounded-lg"><div className="flex justify-between items-start"><div><p className="font-medium text-gray-900">{cr.title}</p><p className="text-xs text-gray-500">Requested: {format(new Date(cr.requestDate), 'MMM dd, yyyy')}</p></div><span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(cr.status)}`}>{cr.status}</span></div></div>
              )) : <p className="text-sm text-gray-500 text-center py-4">No change requests found.</p>}
            </div>
          </Card>

          <Card className="p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center"><SafeIcon icon={FiCheckSquare} className="h-5 w-5 mr-2" />Tasks</h2>
            <div className="space-y-3 max-h-64 overflow-y-auto">
              {clientTasks.length > 0 ? clientTasks.map(t => (
                <div key={t.id} className="p-3 bg-gray-50 rounded-lg"><div className="flex justify-between items-start"><div><p className="font-medium text-gray-900">{t.title}</p><p className="text-xs text-gray-500">Project/Ref: {t.projectId || 'N/A'}</p></div><span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(t.status)}`}>{t.status.replace('-', ' ')}</span></div></div>
              )) : <p className="text-sm text-gray-500 text-center py-4">No related tasks found.</p>}
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default ClientProfile;